import React from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  Banner,
  Button,
  DocsAnnotation,
  LoadingOrbit,
  Text,
  type ScreenshotItem,
} from '@chatsdet/ui';
import { ArrowLeft, CaretDown, Pulse } from '@phosphor-icons/react';
import { motion, useReducedMotion } from 'framer-motion';
import type { ExecutionEvent } from '../../../components/ExecutionMonitor';
import {
  useDeleteWorkflowRun,
  useWorkflowRuns,
  useOptimizationRuns,
} from '../hooks/useWorkflowRuns';
import { useWorkflowRun } from '../hooks/useWorkflowRun';
import { RunHistoryList } from './RunHistoryList';
import { RunDetailsPanel } from './RunDetailsPanel';
import { LiveRunPanel } from './LiveRunPanel';
import { ConvergenceRunHistoryCard } from './ConvergenceRunHistoryCard';
import { ConvergenceRunDetailPanel } from './ConvergenceRunDetailPanel';
import { getLatestNonTerminalStatusEvent } from './live-run-status';
import type { WorkflowNode } from '../../../../types/workflow';
import type { FlowDesignerSearchParams } from '../../../../router';

const cleanSearch = (search: FlowDesignerSearchParams): FlowDesignerSearchParams => {
  const cleaned: FlowDesignerSearchParams = {};
  if (typeof search.runId === 'string' && search.runId.trim().length > 0) {
    cleaned.runId = search.runId;
  }
  if (cleaned.runId && search.runView === 'live') {
    cleaned.runView = 'live';
  }
  if (cleaned.runId && search.expandedVideo === true) {
    cleaned.expandedVideo = true;
  }
  if (typeof search.convergenceRunId === 'string' && search.convergenceRunId.trim().length > 0) {
    cleaned.convergenceRunId = search.convergenceRunId;
  }
  if (
    typeof search.runsPage === 'number' &&
    Number.isFinite(search.runsPage) &&
    search.runsPage > 1
  ) {
    cleaned.runsPage = Math.floor(search.runsPage);
  }
  if (
    typeof search.runsPageSize === 'number' &&
    Number.isFinite(search.runsPageSize) &&
    search.runsPageSize > 0 &&
    search.runsPageSize !== 10
  ) {
    cleaned.runsPageSize = Math.floor(search.runsPageSize);
  }
  if (
    typeof search.optimizationPage === 'number' &&
    Number.isFinite(search.optimizationPage) &&
    search.optimizationPage > 1
  ) {
    cleaned.optimizationPage = Math.floor(search.optimizationPage);
  }
  return cleaned;
};

type RunsTabPanelProps = {
  workflowId: string | null;
  nodes: WorkflowNode[];
  isActive: boolean;
  live: {
    isRunning: boolean;
    isReconnecting: boolean;
    runId: string | null;
    events: ExecutionEvent[];
    currentStepId: string | null;
    screenshotItems: ScreenshotItem[];
    reconnectToRun: (runId: string) => void;
  };
  /** Builder mode - for AI-generated workflows */
  builder?: {
    isGenerating: boolean;
    streamId: string | null;
    nodeCount: number;
    onCancel?: () => void;
  };
  /** Callback when the selected run's failed node changes (for canvas highlighting) */
  onFailedNodeChange?: (nodeId: string | null) => void;
  /** Open cache debug overlay for a specific run */
  onOpenCacheDebug?: (runId: string) => void;
  /** Threaded to RunDetailsPanel's "Link issue" → file-new-issue mode. */
  propertyId: string | null;
};

export const RunsTabPanel = React.memo(function RunsTabPanel({
  workflowId,
  nodes,
  isActive,
  live,
  builder,
  onFailedNodeChange,
  onOpenCacheDebug,
  propertyId,
}: RunsTabPanelProps) {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { workflowId?: string };
  const effectiveWorkflowId = workflowId ?? params.workflowId ?? null;

  const searchParams = useSearch({ strict: false }) as FlowDesignerSearchParams;
  const page = searchParams.runsPage ?? 1;
  const pageSize = searchParams.runsPageSize ?? 10;
  const optimizationPage = searchParams.optimizationPage ?? 1;
  const selectedRunId = typeof searchParams.runId === 'string' ? searchParams.runId : null;
  const selectedConvergenceRunId =
    typeof searchParams.convergenceRunId === 'string' ? searchParams.convergenceRunId : null;
  const selectedRunView = searchParams.runView === 'live' ? 'live' : 'details';
  const reduceMotion = useReducedMotion();
  const isBuilderActive = Boolean(builder?.isGenerating && builder.streamId);
  const showDetail = Boolean(selectedRunId) || isBuilderActive || Boolean(selectedConvergenceRunId);
  const showConvergenceDetail = selectedConvergenceRunId !== null && !selectedRunId && !isBuilderActive;
  const [showOptimizationHistory, setShowOptimizationHistory] = React.useState(false);
  const queryClient = useQueryClient();
  const latestStatusEvent = React.useMemo(
    () => getLatestNonTerminalStatusEvent(live.events),
    [live.events]
  );

  // Track previous runId to detect new runs starting
  const prevRunIdRef = React.useRef<string | null>(null);

  const updateSearchParams = React.useCallback(
    (updates: Partial<FlowDesignerSearchParams>) => {
      if (!effectiveWorkflowId) return;
      navigate({
        to: '/flows/$workflowId',
        params: { workflowId: effectiveWorkflowId },
        search: (prev: FlowDesignerSearchParams) => cleanSearch({ ...prev, ...updates }),
        replace: true,
      });
    },
    [navigate, effectiveWorkflowId]
  );

  // Browser flows filter out api_sequence (seed) runs.
  const runsQ = useWorkflowRuns(effectiveWorkflowId, {
    page,
    pageSize,
    mode: 'browser' as const,
  });
  const optimizationHistoryQ = useOptimizationRuns(
    effectiveWorkflowId,
    {
      page: optimizationPage,
      pageSize: 10,
    },
    { enabled: showOptimizationHistory }
  );
  const runs = runsQ.data?.data.runs ?? [];
  const optimizationRuns = optimizationHistoryQ.data?.data.runs ?? [];
  const optimizationPagination = optimizationHistoryQ.data?.pagination ?? {
    page: optimizationPage,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  };
  const selectedConvergenceRun = React.useMemo(
    () =>
      selectedConvergenceRunId
        ? (optimizationRuns.find((run) => run.id === selectedConvergenceRunId) ?? null)
        : null,
    [optimizationRuns, selectedConvergenceRunId]
  );

  // Auto-navigate to live view when a new run starts
  React.useEffect(() => {
    // Detect transition: new run started (null → runId, or different runId)
    if (live.runId && live.isRunning && live.runId !== prevRunIdRef.current) {
      updateSearchParams({ runId: live.runId, runView: 'live', runsPage: 1 });
    }
    prevRunIdRef.current = live.runId;
  }, [live.runId, live.isRunning, updateSearchParams]);

  const runQ = useWorkflowRun(effectiveWorkflowId, selectedRunId);
  const deleteRunMutation = useDeleteWorkflowRun(effectiveWorkflowId);

  React.useEffect(() => {
    if (!isActive || !effectiveWorkflowId) return;
    void queryClient.invalidateQueries({ queryKey: ['workflow-runs', effectiveWorkflowId] });
    void queryClient.invalidateQueries({
      queryKey: ['optimization-runs', effectiveWorkflowId],
    });
    if (selectedRunId) {
      void queryClient.invalidateQueries({
        queryKey: ['workflow-run', effectiveWorkflowId, selectedRunId],
      });
    }
  }, [isActive, effectiveWorkflowId, selectedRunId, queryClient]);

  // Invalidate run queries when execution completes
  React.useEffect(() => {
    if (!effectiveWorkflowId || !selectedRunId) return;
    if (live.isRunning) return; // Still running, don't invalidate yet

    // Check if the last event indicates completion (success, error, or step failure)
    const lastEvent = live.events[live.events.length - 1];
    const isCompleted =
      lastEvent?.type === 'complete' ||
      lastEvent?.type === 'error' ||
      lastEvent?.type === 'step-failed';

    if (isCompleted) {
      void queryClient.invalidateQueries({
        queryKey: ['workflow-run', effectiveWorkflowId, selectedRunId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['workflow-runs', effectiveWorkflowId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['optimization-runs', effectiveWorkflowId],
      });
    }
  }, [live.isRunning, live.events, effectiveWorkflowId, selectedRunId, queryClient]);

  // Notify parent of failed node when viewing a historical failed run
  React.useEffect(() => {
    if (!onFailedNodeChange) return;

    // During live execution, don't override with historical data
    if (live.isRunning && live.runId === selectedRunId) {
      return;
    }

    const run = runQ.data?.data.run;
    if (!run || run.status !== 'failed') {
      onFailedNodeChange(null);
      return;
    }

    const failedStep = run.steps.find((s) => s.status === 'failed');
    onFailedNodeChange(failedStep?.nodeId ?? null);
  }, [runQ.data, selectedRunId, live.isRunning, live.runId, onFailedNodeChange]);

  const onBackToRuns = React.useCallback(() => {
    updateSearchParams({ runId: undefined, runView: undefined, convergenceRunId: undefined });
    if (effectiveWorkflowId) {
      void queryClient.invalidateQueries({ queryKey: ['workflow-runs', effectiveWorkflowId] });
      void queryClient.invalidateQueries({
        queryKey: ['optimization-runs', effectiveWorkflowId],
      });
    }
  }, [updateSearchParams, effectiveWorkflowId, queryClient]);

  const openRunDetails = React.useCallback(
    (runId: string) => updateSearchParams({ runId, runView: undefined, convergenceRunId: undefined }),
    [updateSearchParams]
  );

  const openRunLive = React.useCallback(() => {
    if (!live.runId) return;
    updateSearchParams({ runId: live.runId, runView: 'live' });
  }, [live.runId, updateSearchParams]);

  const openConvergenceRun = React.useCallback(
    (runId: string) => updateSearchParams({ convergenceRunId: runId, runId: undefined }),
    [updateSearchParams]
  );

  const closeConvergenceRun = React.useCallback(
    () => updateSearchParams({ convergenceRunId: undefined }),
    [updateSearchParams]
  );

  if (!effectiveWorkflowId) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Text size="sm" className="text-muted-foreground">
          Select a workflow to view runs
        </Text>
      </div>
    );
  }

  const pagination = runsQ.data?.pagination ?? { page, pageSize, totalItems: 0, totalPages: 0 };

  return (
    <DocsAnnotation slug="reference/runs" title="Runs">
      <div className="h-full overflow-hidden">
        <div className="relative h-full">
          <motion.div
            className="absolute inset-0 flex flex-col h-full"
            initial={false}
            animate={
              reduceMotion
                ? { opacity: showDetail ? 0 : 1 }
                : { opacity: showDetail ? 0 : 1, x: showDetail ? -48 : 0 }
            }
            transition={
              reduceMotion ? { duration: 0.12 } : { type: 'spring', stiffness: 360, damping: 34 }
            }
            style={{ pointerEvents: showDetail ? 'none' : 'auto' }}
            aria-hidden={showDetail}
          >
            {live.isRunning && live.runId ? (
              <div className="flex justify-end px-4 pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  iconLeft={<Pulse size={16} weight="bold" />}
                  onPress={openRunLive}
                >
                  View live
                </Button>
              </div>
            ) : null}

            {/* No `py-*`: the first row sits flush under the tab strip, the
                same way the list now runs flush to the panel's side edges. */}
            <div className="flex-1 overflow-y-auto">
              {runsQ.isLoading ? (
                <div className="flex items-center justify-center py-10">
                  <LoadingOrbit />
                </div>
              ) : (
                <RunHistoryList
                  runs={runs}
                  pagination={pagination}
                  isLoading={runsQ.isFetching}
                  selectedRunId={null}
                  onSelectRun={openRunDetails}
                  onDeleteRun={(runId) => deleteRunMutation.mutate(runId)}
                  onPageChange={(nextPage) => updateSearchParams({ runsPage: nextPage })}
                  onPageSizeChange={(nextPageSize) =>
                    updateSearchParams({ runsPageSize: nextPageSize, runsPage: 1 })
                  }
                />
              )}
              <div className="border-t border-border/50">
                <Button
                  variant="ghost"
                  className="w-full justify-between rounded-none px-4 py-3"
                  aria-expanded={showOptimizationHistory}
                  onPress={() => setShowOptimizationHistory((open) => !open)}
                >
                  <span>Optimization history</span>
                  <CaretDown
                    size={16}
                    className={
                      showOptimizationHistory
                        ? 'rotate-180 transition-transform'
                        : 'transition-transform'
                    }
                  />
                </Button>
                {showOptimizationHistory ? (
                  <div className="border-t border-border/50">
                    {optimizationHistoryQ.isLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <LoadingOrbit />
                      </div>
                    ) : optimizationHistoryQ.isError ? (
                      <Banner variant="error" className="m-4">
                        <Text size="sm">
                          {optimizationHistoryQ.error instanceof Error
                            ? optimizationHistoryQ.error.message
                            : 'Failed to load optimization history.'}
                        </Text>
                      </Banner>
                    ) : optimizationRuns.length > 0 ? (
                      <div className="divide-y divide-border/50">
                        {optimizationRuns.map((run) => (
                          <div key={run.id} className="group">
                            <ConvergenceRunHistoryCard
                              run={run}
                              isSelected={selectedConvergenceRunId === run.id}
                              onSelect={openConvergenceRun}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="px-4 py-6 text-center">
                        <Text size="sm" className="text-muted-foreground">
                          No optimization runs yet
                        </Text>
                      </div>
                    )}
                    <div className="border-t border-border/50 px-4 py-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            isDisabled={
                              optimizationPagination.page <= 1 || optimizationHistoryQ.isFetching
                            }
                            onPress={() =>
                              updateSearchParams({
                                optimizationPage: Math.max(1, optimizationPagination.page - 1),
                              })
                            }
                          >
                            Prev
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            isDisabled={
                              optimizationPagination.totalPages === 0 ||
                              optimizationPagination.page >= optimizationPagination.totalPages ||
                              optimizationHistoryQ.isFetching
                            }
                            onPress={() =>
                              updateSearchParams({
                                optimizationPage: optimizationPagination.page + 1,
                              })
                            }
                          >
                            Next
                          </Button>
                          <Text size="xs" className="text-muted-foreground">
                            {optimizationPagination.page} /{' '}
                            {Math.max(1, optimizationPagination.totalPages)}
                          </Text>
                        </div>
                        <Text size="xs" className="text-muted-foreground">
                          {optimizationPagination.totalItems} total
                        </Text>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </motion.div>

          <motion.div
            className="absolute inset-0 h-full"
            initial={false}
            animate={
              reduceMotion
                ? { opacity: showDetail ? 1 : 0 }
                : { opacity: showDetail ? 1 : 0, x: showDetail ? 0 : 48 }
            }
            transition={
              reduceMotion ? { duration: 0.12 } : { type: 'spring', stiffness: 360, damping: 34 }
            }
            style={{ pointerEvents: showDetail ? 'auto' : 'none' }}
            aria-hidden={!showDetail}
          >
            {showConvergenceDetail && selectedConvergenceRunId ? (
              <ConvergenceRunDetailPanel
                runId={selectedConvergenceRunId}
                summary={selectedConvergenceRun}
                onBack={closeConvergenceRun}
              />
            ) : builder?.isGenerating && builder.streamId ? (
              <LiveRunPanel
                run={null}
                onBack={onBackToRuns}
                onShowDetails={() => {}}
                live={{
                  isRunning: false,
                  isReconnecting: false,
                  currentStepId: null,
                  events: [],
                }}
                builder={{
                  streamId: builder.streamId,
                  workflowId: effectiveWorkflowId ?? '',
                  nodeCount: builder.nodeCount,
                  onCancel: builder.onCancel,
                }}
              />
            ) : selectedRunId ? (
              /* Show live view when: (a) run is actively live, or (b) URL says runView=live AND
               run is still in progress (not yet in a terminal state).
               Skip live view for API-only runs — they have no browser stream. */
              !runQ.data?.data.run?.metadataJson?.isApiOnly &&
              ((selectedRunId === live.runId && live.isRunning) ||
                (selectedRunView === 'live' &&
                  (!runQ.data?.data.run ||
                    runQ.data.data.run.status === 'running' ||
                    runQ.data.data.run.status === 'queued'))) ? (
                <LiveRunPanel
                  runId={selectedRunId}
                  run={runQ.data?.data.run ?? null}
                  onBack={onBackToRuns}
                  onShowDetails={() => updateSearchParams({ runView: undefined })}
                  live={{
                    isRunning: live.isRunning && live.runId === selectedRunId,
                    isReconnecting: live.isReconnecting && live.runId === selectedRunId,
                    currentStepId: live.runId === selectedRunId ? live.currentStepId : null,
                    events: live.runId === selectedRunId ? live.events : [],
                  }}
                  statusEvent={live.runId === selectedRunId ? latestStatusEvent : null}
                />
              ) : runQ.isLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <LoadingOrbit />
                </div>
              ) : !runQ.data?.data.run ? (
                <div className="flex flex-col h-full">
                  <div className="px-4 py-4 border-b border-border">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconLeft={<ArrowLeft size={16} />}
                      onPress={onBackToRuns}
                    >
                      Runs
                    </Button>
                  </div>
                  <div className="flex-1 flex items-center justify-center">
                    <Text size="sm" className="text-muted-foreground">
                      Run not found
                    </Text>
                  </div>
                </div>
              ) : (
                <RunDetailsPanel
                  run={runQ.data.data.run}
                  nodes={nodes}
                  onBack={onBackToRuns}
                  onOpenCacheDebug={onOpenCacheDebug}
                  propertyId={propertyId}
                />
              )
            ) : null}
          </motion.div>
        </div>
      </div>
    </DocsAnnotation>
  );
});
